import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: number;
  title: string;
  description: string;
  iconName: string;
  features: string[];
  priority?: boolean;
}

const services: Service[] = [
  {
    id: 1,
    title: "Building Maintenance",
    description: "Comprehensive building maintenance services to keep your commercial property in optimal condition year-round.",
    iconName: "BuildingOfficeIcon",
    features: ["General Repairs", "Structural Care", "Preventative Maintenance", "System Checks"],
    priority: true
  },
  {
    id: 2,
    title: "Temporary Staffing",
    description: "Certified 4th and 5th class power engineers available for temporary placements and emergency coverage.",
    iconName: "UserGroupIcon",
    features: ["Power Engineers", "Licensed Professionals", "Flexible Scheduling", "Emergency Coverage"],
    priority: true
  },
  {
    id: 3,
    title: "Parkade Power Sweeping",
    description: "Professional parkade and parking lot cleaning services to maintain clean, safe environments for tenants and visitors.",
    iconName: "TruckIcon",
    features: ["Power Sweeping", "Debris Removal", "Regular Scheduling", "Post-Construction Cleanup"],
    priority: true
  },
  {
    id: 4,
    title: "Window Cleaning",
    description: "Professional highrise and lowrise exterior and interior window cleaning for crystal-clear views and enhanced curb appeal.",
    iconName: "WindowIcon",
    features: ["Highrise Exterior", "Lowrise Buildings", "Interior Cleaning", "Streak-Free Results"],
    priority: true
  },
  {
    id: 5,
    title: "Pressure Washing",
    description: "High-quality pressure washing services for building exteriors, walkways, and surfaces to restore and maintain appearance.",
    iconName: "SparklesIcon",
    features: ["Exterior Cleaning", "Surface Restoration", "Graffiti Removal", "Preventative Care"]
  },
  {
    id: 6,
    title: "Drywall & Painting",
    description: "Expert drywall repair and professional painting services for both interior and exterior commercial spaces.",
    iconName: "PaintBrushIcon",
    features: ["Interior Painting", "Exterior Coating", "Drywall Repair", "Surface Preparation"]
  },
  {
    id: 7,
    title: "Handyman Service",
    description: "Versatile handyman services for all those small to medium repairs and improvements your property needs.",
    iconName: "WrenchScrewdriverIcon",
    features: ["General Repairs", "Fixture Installation", "Minor Renovations", "Quick Response"]
  },
  {
    id: 8,
    title: "Electrical Services",
    description: "Professional electrical installation and maintenance services including supply and ongoing support.",
    iconName: "BoltIcon",
    features: ["Installation", "Maintenance", "Supply Services", "Emergency Repairs"]
  },
  {
    id: 9,
    title: "Concrete Repair",
    description: "Minor concrete repair and forming services to address cracks, damage, and structural needs.",
    iconName: "CubeIcon",
    features: ["Crack Repair", "Surface Forming", "Structural Fixes", "Preventative Care"]
  },
  {
    id: 10,
    title: "HVAC Filter Supply",
    description: "Reliable HVAC filter supply services to ensure your building's air quality and system efficiency.",
    iconName: "AdjustmentsHorizontalIcon",
    features: ["Filter Supply", "Regular Delivery", "Custom Sizing", "Quality Products"]
  },
  {
    id: 11,
    title: "Woodwork Services",
    description: "Professional woodwork services including repairs, installations, and custom carpentry solutions.",
    iconName: "HomeModernIcon",
    features: ["Repairs", "Custom Work", "Installation", "Refinishing"]
  },
  {
    id: 12,
    title: "Junk Removal",
    description: "Efficient junk removal services for projects of any size, from small cleanouts to large-scale removals.",
    iconName: "TrashIcon",
    features: ["Small Removals", "Large Projects", "Debris Cleanup", "Eco-Friendly Disposal"]
  },
  {
    id: 13,
    title: "Janitorial Services",
    description: "Comprehensive commercial cleaning including interior window cleaning to maintain professional, healthy environments.",
    iconName: "HomeModernIcon",
    features: ["Daily Cleaning", "Interior Windows", "Floor Care", "Waste Management"]
  },
  {
    id: 14,
    title: "Preventative Maintenance",
    description: "Customized preventative maintenance programs designed to prevent costly repairs and extend asset lifespan.",
    iconName: "ClipboardDocumentCheckIcon",
    features: ["Scheduled Inspections", "Maintenance Plans", "Budget Forecasting", "Compliance Tracking"]
  }
];

export default function ServicesOverview() {
  return (
    <section className="py-20 md:py-28 bg-background relative">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-b from-surface/50 to-transparent pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm font-medium text-primary mb-6">
            <Icon name="WrenchScrewdriverIcon" size={16} variant="solid" />
            <span>Comprehensive Solutions</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6 leading-tight">
            Full-Service Property Maintenance
          </h2>
          <p className="text-lg text-text-secondary leading-relaxed">
            From routine maintenance to specialized services, we're your single-source solution for all commercial property needs across the Lower Mainland.
          </p>
        </div>
        
        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {services.map((service) => (
            <div
              key={service.id}
              className={`group relative bg-white border rounded-2xl p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                service.priority 
                  ? 'border-primary/30 hover:border-primary shadow-md' 
                  : 'border-border hover:border-primary/50 shadow-sm'
              }`}
            >
              {service.priority && (
                <div className="absolute -top-3 -right-3 px-3 py-1 bg-primary text-primary-foreground text-xs font-bold rounded-full shadow-lg">
                  Featured
                </div>
              )}

              <div className="flex items-start gap-4 mb-4">
                <div className={`p-3 rounded-xl transition-all duration-300 ${
                  service.priority
                    ? 'bg-primary/10 group-hover:bg-primary group-hover:scale-110'
                    : 'bg-surface group-hover:bg-primary/10 group-hover:scale-110'
                }`}>
                  <Icon 
                    name={service.iconName as any} 
                    size={28} 
                    variant="outline" 
                    className={`transition-colors duration-300 ${
                      service.priority
                        ? 'text-primary group-hover:text-primary-foreground'
                        : 'text-primary group-hover:text-primary'
                    }`}
                  />
                </div>
              </div>
              
              <h3 className="font-heading text-xl font-bold text-text-primary mb-3 group-hover:text-primary transition-colors duration-300">
                {service.title}
              </h3>
              
              <p className="text-text-secondary mb-4 leading-relaxed text-sm">
                {service.description}
              </p>
              
              <ul className="space-y-2 mb-6">
                {service.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-text-secondary">
                    <Icon name="CheckIcon" size={16} variant="solid" className="text-success flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Link
                href="/services"
                className="inline-flex items-center gap-2 text-primary font-semibold text-sm group-hover:gap-3 transition-all duration-300"
              >
                <span>Learn More</span>
                <Icon name="ArrowRightIcon" size={16} variant="outline" />
              </Link>
            </div>
          ))}
        </div>
        
        {/* CTA Section */}
        <div className="text-center">
          <Link
            href="/services"
            className="inline-flex items-center gap-2 px-8 py-4 bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/90 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl hover:scale-105"
          >
            <span>View All Services</span>
            <Icon name="ArrowRightIcon" size={20} variant="outline" />
          </Link>
        </div>
      </div>
    </section>
  );
}
